import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { CommonService } from "../services/CommonService";
import {
  IIeps,
  IIva,
  IProduct,
  IResultPagination,
  ISupplier,
  ProductPO,
} from "../common/interfaces";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import PoProduct from "./PoProduct";
import { useLocalStorage } from "../auth/useLocalStorage";
import Swal from "sweetalert2";
import "@sweetalert2/theme-dark/dark.css"
import withReactContent from "sweetalert2-react-content";
import DeleteIcon from "@mui/icons-material/Delete";
import { usePosStore } from "../context/posStore";
import {
  createCalculationScheduler,
  EMPTY_CALCULATION,
  lineFor,
  PurchaseOrderCalculation,
  toCalculationItems,
} from "../services/purchaseOrderCalculation";
const MySwal = withReactContent(Swal);

export interface CreatePOProps {
  supplierId?: number;
  handleCancel: () => void;
}

const money = (value?: number) => Number(value || 0).toFixed(2);

export default function CreatePO(props: CreatePOProps) {
  const [user, setUser] = useLocalStorage("user", null);
  const purchaseOrder = usePosStore((state) => state.purchaseOrder);
  const setPurchaseOrder = usePosStore((state) => state.setPurchaseOrder);
  const clearPurchaseOrder = usePosStore((state) => state.clearPurchaseOrder);
  const [supplier, setSupplier] = useState<ISupplier>();
  const [openModal, setOpenModal] = useState(false);
  const [products, setProducts] = useState<Array<ProductPO>>([]);
  const [calculation, setCalculation] = useState<PurchaseOrderCalculation>(EMPTY_CALCULATION);
  const [calculating, setCalculating] = useState(false);
  const [ivaCatalog, setIvaCatalog] = useState<IIva[]>([]);
  const [iepsCatalog, setIepsCatalog] = useState<IIeps[]>([]);
  // Impuestos y totales los calcula el back; aqui solo se piden y se pintan.
  const scheduler = useRef<ReturnType<typeof createCalculationScheduler>>();
  if (!scheduler.current) {
    scheduler.current = createCalculationScheduler({
      calculate: (payload) => CommonService.calculatePO(payload),
      onResult: setCalculation,
      onError: () => CommonService.toast("No se pudo calcular el total de la orden", "error"),
      onBusyChange: setCalculating,
    });
  }
  // Cargar catálogos de IVA e IEPS al iniciar
  useEffect(() => {
    CommonService.getIvaCatalog().then((res) => {
      console.log('📊 Catálogo IVA recibido:', JSON.stringify(res, null, 2));
      console.log('📊 res.success:', res.success, 'res.object:', res.object);
      // El backend devuelve un array directamente, no un objeto con success/object
      if (Array.isArray(res)) {
        const normalizedCatalog = res.map((item: any) => ({
          ...item,
          porcentaje: typeof item.porcentaje === 'string' ? parseFloat(item.porcentaje) : item.porcentaje
        }));
        console.log('✅ Catálogo IVA normalizado:', JSON.stringify(normalizedCatalog, null, 2));
        setIvaCatalog(normalizedCatalog);
      } else if (res.success && res.object) {
        const normalizedCatalog = res.object.map((item: any) => ({
          ...item,
          porcentaje: typeof item.porcentaje === 'string' ? parseFloat(item.porcentaje) : item.porcentaje
        }));
        console.log('✅ Catálogo IVA normalizado:', JSON.stringify(normalizedCatalog, null, 2));
        setIvaCatalog(normalizedCatalog);
      }
    }).catch((err) => {
      console.error('❌ Error al cargar catálogo IVA:', err);
    });
    CommonService.getIepsCatalog().then((res) => {
      console.log('📊 Catálogo IEPS recibido:', JSON.stringify(res, null, 2));
      console.log('📊 res.success:', res.success, 'res.object:', res.object);
      // El backend devuelve un array directamente, no un objeto con success/object
      if (Array.isArray(res)) {
        const normalizedCatalog = res.map((item: any) => ({
          ...item,
          porcentaje: typeof item.porcentaje === 'string' ? parseFloat(item.porcentaje) : item.porcentaje
        }));
        console.log('✅ Catálogo IEPS normalizado:', JSON.stringify(normalizedCatalog, null, 2));
        setIepsCatalog(normalizedCatalog);
      } else if (res.success && res.object) {
        const normalizedCatalog = res.object.map((item: any) => ({
          ...item,
          porcentaje: typeof item.porcentaje === 'string' ? parseFloat(item.porcentaje) : item.porcentaje
        }));
        console.log('✅ Catálogo IEPS normalizado:', JSON.stringify(normalizedCatalog, null, 2));
        setIepsCatalog(normalizedCatalog);
      }
    }).catch((err) => {
      console.error('❌ Error al cargar catálogo IEPS:', err);
    });
  }, []);

  // Los productos ya no dependen de los catálogos: antes este efecto corría de nuevo al llegar los
  // catálogos y restauraba de caché partidas armadas sin IVA.
  useEffect(() => {
    if (props.supplierId) {
      // Verificar si hay progreso guardado en caché
      if(purchaseOrder[props.supplierId] && purchaseOrder[props.supplierId].products?.length > 0){
        setProducts(purchaseOrder[props.supplierId].products);
        setSupplier(purchaseOrder[props.supplierId].supplier);
      } else {
        // Si no hay caché, siempre obtener datos frescos del backend
        CommonService.getSupplierById(props.supplierId).then((value: ISupplier) => {
          setSupplier(value);
          setPurchaseOrder(`${props.supplierId}`, {
            ...purchaseOrder[`${props.supplierId}`],
            supplier: value,
            products: []
          });
        });

        // La sucursal de la URL ya no cuenta (el back usa la de la sesión); se sigue mandando mientras sale el back nuevo
        CommonService.createPOBySupplier(props.supplierId, user.warehouse_id).then((value) => {
          const {success, object: products, message} = value
          if(success){
            // Cada producto llega con el IVA/IEPS de compra que le asigna el back
            setProducts(products || []);
            return;
          }
          CommonService.toast(message || "No se pudieron cargar los productos del proveedor", "error")
        })
      }
    }
  }, [props.supplierId]);

  useEffect(() => {
    scheduler.current?.schedule({ items: toCalculationItems(products) });
  }, [products]);

  useEffect(() => () => scheduler.current?.cancel(), []);

  useEffect(() => {
    // Guardar progreso en caché cada vez que cambian los productos
    if(supplier?.id && products.length > 0){
      setPurchaseOrder(`${supplier.id}`, {
        ...purchaseOrder[`${supplier.id}`],
        supplier,
        products
      });
    }
  }, [products]);

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const style = {
    position: "relative",
    maxHeight: "700px",
    overflow: "auto",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "#333",
    // border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };
  const addProduct = (product: ProductPO) => {
    setOpenModal(false);
    if (products.find((p) => p.product.id === product.product.id)) {
      return;
    }
    const newProducts = [...products, product];
    setProducts(newProducts);
    // Guardar en caché
    setPurchaseOrder(`${supplier?.id}`, {
      ...purchaseOrder[`${supplier?.id}`],
      products: newProducts
    });
  };

  const updateProduct = (productId: number, changes: Partial<ProductPO>) => {
    setProducts(products.map((p) => (p.product.id === productId ? { ...p, ...changes } : p)));
  };

  const createPosPO = async () => {
    if(products.length === 0){
      CommonService.toast("No hay productos agregados", "error")
      return;
    }
    if (calculating) {
      return;
    }
    const validatePrices = products.every((product) => (lineFor(calculation, product)?.total ?? 0) > 0)
    if(!validatePrices){
      CommonService.toast("El total de cada producto debe ser mayor a 0", "error")
      return;
    }
    // const {success, object} = await CommonService.getAmountCash(user?.cbox.id);
    // console.log({object})
    // return

    // Solo lo capturado: el back calcula impuestos y totales al guardar.
    const payload = {
      products: products.map(p => ({
        product: { id: p.product.id },
        quantity: p.quantity,
        price: p.price,
        iva_id: p.iva_id ?? null,
        ieps_id: p.ieps_id ?? null,
      })),
      supplier_id: supplier?.id,
      // user y warehouses ya no cuentan (el back los toma de la sesión); se siguen mandando mientras sale el back nuevo
      user: user?.id,
      cashBox: user?.cbox.id,
      warehouses: user?.warehouse_id,
      credit: Number(supplier?.dias) > 0 ? 1 : 0,
    };
    CommonService.createPO(payload).then((res) => {
      if(res.success){
        CommonService.toast("Orden de compra creada con exito", "success")
        setOpenModal(false);
        setProducts([]);
        props.handleCancel();
        // Limpiar caché después de crear exitosamente
        clearPurchaseOrder(`${supplier?.id}`);
        CommonService.printTicketPO(res.object.id).then((res: any) => console.log(res))
        return;
      }
      CommonService.toast(res.message || "Error al crear la orden de compra", "error")
    })

  }

  const removeItem = async (itemId: number) => {
    const indexToRemove = products.findIndex((item) => item.product.id === itemId);
    if(indexToRemove > -1){
      const aux = [...products];
      aux.splice(indexToRemove, 1);
      setProducts(aux);
      // Actualizar caché
      setPurchaseOrder(`${supplier?.id}`, {
        ...purchaseOrder[`${supplier?.id}`],
        products: aux
      });
    }
  }
  return (
    <div>
      <h4>
        Proveedor: {supplier?.nombre}
        <br />Tipo de pago: {Number(supplier?.dias) > 0 ? 'CREDITO' : 'CONTADO'}
        <Button
          style={{ float: "right" }}
          hidden={true}
          onClick={() => setOpenModal(true)}
        >
          Agregar Producto
        </Button>
      </h4>
      <div>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">IVA</TableCell>
                <TableCell align="right">IEPS</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell align="right">Impuestos</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((row) => (
                <TableRow
                  key={row.product.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.product.name}
                  </TableCell>
                  <TableCell align="right">{row.price.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <FormControl fullWidth style={{ marginTop: "10px", minWidth: "120px" }}>
                      <InputLabel id={`iva-label-${row.product.id}`}>IVA</InputLabel>
                      <Select
                        labelId={`iva-label-${row.product.id}`}
                        value={row.iva_id ?? ''}
                        label="IVA"
                        onChange={(e) => updateProduct(row.product.id, { iva_id: e.target.value ? Number(e.target.value) : null })}
                      >
                        <MenuItem value="">Sin IVA (0%)</MenuItem>
                        {ivaCatalog.length > 0 ? ivaCatalog.map((iva) => (
                          <MenuItem key={iva.id} value={iva.id}>
                            {iva.nombre || `IVA ${(iva.porcentaje * 100).toFixed(0)}%`} ({(iva.porcentaje * 100).toFixed(0)}%)
                          </MenuItem>
                        )) : <MenuItem disabled>Cargando...</MenuItem>}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">
                    <FormControl fullWidth style={{ marginTop: "10px", minWidth: "120px" }}>
                      <InputLabel id={`ieps-label-${row.product.id}`}>IEPS</InputLabel>
                      <Select
                        labelId={`ieps-label-${row.product.id}`}
                        value={row.ieps_id ?? ''}
                        label="IEPS"
                        onChange={(e) => updateProduct(row.product.id, { ieps_id: e.target.value ? Number(e.target.value) : null })}
                      >
                        <MenuItem value="">Sin IEPS (0%)</MenuItem>
                        {iepsCatalog.length > 0 ? iepsCatalog.map((ieps) => (
                          <MenuItem key={ieps.id} value={ieps.id}>
                            {ieps.nombre || `IEPS ${(ieps.porcentaje * 100).toFixed(0)}%`} ({(ieps.porcentaje * 100).toFixed(0)}%)
                          </MenuItem>
                        )) : <MenuItem disabled>Cargando...</MenuItem>}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">
                    <Input
                      type="number"
                      value={row.quantity || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;

                        // Permitir campo vacío temporalmente
                        if (inputValue === '') {
                          updateProduct(row.product.id, { quantity: '' as any });
                          return;
                        }

                        // Convertir a número decimal
                        let numValue = parseFloat(inputValue);

                        // Validar que sea un número válido y mayor o igual a 0
                        if (isNaN(numValue) || numValue < 0) {
                          return; // No actualizar si el valor no es válido
                        }

                        updateProduct(row.product.id, { quantity: numValue });
                      }}
                      onBlur={(e) => {
                        // Al perder el foco, si está vacío, poner en 0
                        if (e.target.value === '') {
                          updateProduct(row.product.id, { quantity: 0 });
                        }
                      }}
                      inputProps={{
                        min: 0,
                        step: 1
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{money(lineFor(calculation, row)?.total)}</TableCell>
                  <TableCell align="right">{money(lineFor(calculation, row)?.tax_amount)}</TableCell>
                  <TableCell>
                  <IconButton
                    aria-label="delete"
                    onClick={() => removeItem(row.product.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={5}></TableCell>
                <TableCell>Subtotal</TableCell>
                <TableCell align="right">{money(calculation.subtotal)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5}></TableCell>
                <TableCell>IVA</TableCell>
                <TableCell align="right">{money(calculation.iva)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5}></TableCell>
                <TableCell>IEPS</TableCell>
                <TableCell align="right">{money(calculation.ieps)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5}></TableCell>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>{money(calculation.grand_total)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <div style={{ marginTop: "30px" }}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button variant="contained" color="error" onClick={() => props.handleCancel()}>
              Cancelar
            </Button>
            <Button variant="contained" color="success" disabled={calculating} onClick={() => createPosPO()}>
              Crear Orden de Compra
            </Button>
          </Stack>
        </div>
      </div>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={{ ...style, width: "600px" }}>
          <PoProduct supplierId={supplier?.id} addProduct={addProduct} />
        </Box>
      </Modal>
    </div>
  );
}
